import {
  and,
  asc,
  eq,
  gte,
  lte,
  meal_plan_entry,
  type PowerSyncDrizzleDB,
  recipe,
} from "@mealplan/powersync";
import { liveRecipeFilters } from "../live/filters";

export const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"] as const;
export type MealType = (typeof MEAL_TYPES)[number];

const DAYS_PER_WEEK = 7;
const ISO_DATE_LENGTH = 10;

/** `YYYY-MM-DD` local (sin huso) de un Date. */
export function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Lunes de la semana que contiene `date` (semana europea). */
export function startOfWeek(date: Date): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekday = (result.getDay() + 6) % DAYS_PER_WEEK; // 0 = lunes
  result.setDate(result.getDate() - weekday);
  return result;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Las 7 fechas (`YYYY-MM-DD`) de la semana que empieza en weekStart. */
export function weekDateKeys(weekStart: Date): string[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_, i) => toDateKey(addDays(weekStart, i)));
}

export function isDateKey(value: string): boolean {
  return value.length === ISO_DATE_LENGTH && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** Entradas del plan en [startDate, endDate] con los datos de su receta. */
export function getMealPlanRangeQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  startDate: string,
  endDate: string,
) {
  return (
    db
      .select({
        id: meal_plan_entry.id,
        plan_date: meal_plan_entry.plan_date,
        meal_type: meal_plan_entry.meal_type,
        recipe_id: meal_plan_entry.recipe_id,
        servings: meal_plan_entry.servings,
        note: meal_plan_entry.note,
        // 🔖 Sello puntero/sello (00050): versión con la que se planificó el
        // plato + puntero a la versión VIVA de la receta. El badge "receta
        // modificada desde que se planificó" es una comparación pura de ids
        // (ver recipeChangedSincePlanned).
        recipe_version_id: meal_plan_entry.recipe_version_id,
        recipe_current_version_id: recipe.current_version_id,
        recipe_name: recipe.name,
        recipe_servings: recipe.servings,
        // 🥗 Nutrición POR PORCIÓN de la receta (00052): el planner la escala
        // por las porciones planificadas (ver nutrition/nutrition.ts).
        recipe_calories: recipe.calories,
        recipe_protein_g: recipe.protein_g,
        recipe_carbs_g: recipe.carbs_g,
        recipe_fat_g: recipe.fat_g,
      })
      .from(meal_plan_entry)
      // El join excluye recetas en papelera: un slot que referencia una receta
      // borrada queda SIN resolver (recipe_name = null) y vuelve a resolverse al
      // restaurarla desde la papelera.
      .leftJoin(recipe, and(eq(meal_plan_entry.recipe_id, recipe.id), ...liveRecipeFilters()))
      .where(
        and(
          eq(meal_plan_entry.user_id, userId),
          gte(meal_plan_entry.plan_date, startDate),
          lte(meal_plan_entry.plan_date, endDate),
        ),
      )
      .orderBy(asc(meal_plan_entry.plan_date), asc(meal_plan_entry.created_at))
  );
}

export type MealPlanEntryWithRecipe = Awaited<
  ReturnType<ReturnType<typeof getMealPlanRangeQuery>["execute"]>
>[number];

/**
 * ¿La receta cambió desde que se planificó este plato? (badge del planner).
 *
 * Comparación pura de ids gracias a la unificación "current-becomes-history":
 * al planificar, el plato sella `recipe_version_id` con la versión current de la
 * receta (== `current_version_id`). Cuando la receta se EDITA, esa fila current
 * se convierte en historial y `recipe.current_version_id` pasa a NULL (o, si se
 * vuelve a sellar otro plato, a una versión distinta). Así:
 *
 *   sellado (recipe_version_id != NULL) Y difiere de la versión viva actual
 *   (current_version_id, que puede ser NULL) ⇒ receta modificada.
 *
 * Los platos legacy sin sello (recipe_version_id == NULL) nunca muestran badge.
 */
export function recipeChangedSincePlanned(entry: {
  recipe_version_id: string | null;
  recipe_current_version_id: string | null;
}): boolean {
  return (
    entry.recipe_version_id != null && entry.recipe_version_id !== entry.recipe_current_version_id
  );
}

/**
 * Entradas del plan (con fecha y tipo de comida) que usan una receta concreta.
 * Interconexión: receta → dónde está planificada (Fase 5, eje 6).
 */
export function getMealPlanByRecipeQuery(db: PowerSyncDrizzleDB, userId: string, recipeId: string) {
  return db
    .select({
      id: meal_plan_entry.id,
      plan_date: meal_plan_entry.plan_date,
      meal_type: meal_plan_entry.meal_type,
      servings: meal_plan_entry.servings,
    })
    .from(meal_plan_entry)
    .where(and(eq(meal_plan_entry.user_id, userId), eq(meal_plan_entry.recipe_id, recipeId)))
    .orderBy(asc(meal_plan_entry.plan_date));
}

export type MealPlanByRecipeRow = Awaited<
  ReturnType<ReturnType<typeof getMealPlanByRecipeQuery>["execute"]>
>[number];
